import { FunctionComponent, useEffect, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';

import { loginAtom } from '../lib/auth';
import { fetch_one, fetch_all } from '../lib/server_helper';
import { checkAllowed } from '../lib/permission';

import PmTrans from '../images/pm_trans.png';

import {
  Avatar,
  Badge,
  Box,
  Divider
} from '@mui/material';

import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
import ArticleIcon from '@mui/icons-material/Article';
import BarChartIcon from '@mui/icons-material/BarChart';
import BusinessIcon from '@mui/icons-material/Business';
import ChecklistIcon from '@mui/icons-material/Checklist';
import CodeIcon from '@mui/icons-material/Code';
import EngineeringIcon from '@mui/icons-material/Engineering';
import EmailIcon from '@mui/icons-material/Email';
import ExtensionIcon from '@mui/icons-material/Extension';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import GetAppIcon from '@mui/icons-material/GetApp';
import GradeIcon from '@mui/icons-material/Grade';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import LinkIcon from '@mui/icons-material/Link';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PieChartIcon from '@mui/icons-material/PieChart';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import SpeedIcon from '@mui/icons-material/Speed';
import StackedLineChartIcon from '@mui/icons-material/StackedLineChart';
import SwitchAccountIcon from '@mui/icons-material/SwitchAccount';
import WorkspacesIcon from '@mui/icons-material/Workspaces';

import { MenuItem } from '../components/sidebar/MenuItem';
import { Profile } from '../components/sidebar/Profile';
import { SidebarCount } from '../models/SidebarCount';
import { SidebarQueues } from './SidebarQueues';

import { getCookie, setCookie } from 'react-use-cookie';

import {
  StyledSelect,
  Typography
} from './interface';

import { Company } from '../models/Company';
import { Project } from '../models/Project';

type SidebarProps = {
  adminAsOrganiser: boolean;
  setAdminAsOrganiser: (b:boolean) => void;
};

export const Sidebar: FunctionComponent<SidebarProps> = ({adminAsOrganiser, setAdminAsOrganiser}) => {
  const theme = useTheme();
  const login = useAtomValue(loginAtom);
  const navigate = useNavigate();
  let is_admin:boolean = login?.is_admin || false;
  let is_organiser:boolean = login?.is_organiser || false;

  const [badgeCounts, setBadgeCounts] = useState<SidebarCount|undefined>(undefined);
  const [searchInterval, setSearchInterval] = useState(setInterval(() => {}, 30 * 1000));
  const [myCompanies, setMyCompanies] = useState<Company[]>([]);
  const [activeCompany, setActiveCompany] = useState<string>(getCookie('panelmetrics_active_company_id') || '');
  const [activeCompanyOb, setActiveCompanyOb] = useState<Company>();
  const [myProjects, setMyProjects] = useState<Project[]>([]);
  const [activeProject, setActiveProject] = useState<string>(getCookie('panelmetrics_active_project_id') || '');
  const [activeProjectOb, setActiveProjectOb] = useState<Project>();

  useEffect(() => {
    clearTimeout(searchInterval);
    if (!!login) {
      onSearch();
      setSearchInterval(setInterval(onSearch, 30 * 1000));
    }
  }, [login]);

  const onSearch = () => {
    if (!!login) {
      // fetch_one<SidebarCount>('/nl/v3/objects/custom_action', {
      //   object: 'user',
      //   class_action: 'custom_api_get_sidebar_counts',
      //   handler_id: login.id
      // }, (obs, complete_data) => {
      //   setBadgeCounts(obs.custom_result);
      // }, (z) => {
  
      // }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
    }
  };

  const fetchMyCompanies = () => {
    fetch_all<Company>('/nl/v3/objects/fetch_all', {
      object: 'company',
      fields: ['id', 'name', 'get_item'],
      handler_id: login?.id
    }, (obs, complete_data) => {
      setMyCompanies(obs);
      setActiveCompanyOb(obs.filter(p => p.id?.toString() === activeCompany)[0]);
      if (obs.length === 1) {
        setActiveCompany(obs[0]?.id?.toString());
        setActiveCompanyOb(obs[0]);
        setCookie('panelmetrics_active_company_id', obs[0]?.id?.toString());
      }
    }, (z) => {
      // setServerErrorSnackOpen(true);
    }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
  };

  const fetchMyProjects = () => {
    fetch_all<Project>('/nl/v3/objects/fetch_all', {
      object: 'project',
      fields: ['id', 'name', 'company|id/name', 'custom_key'],
      handler_id: login?.id
    }, (obs, complete_data) => {
      setMyProjects(obs);
      setActiveProjectOb(obs.filter(p => p.id?.toString() === activeProject)[0]);
      if (obs.length === 1) {
        setActiveProject(obs[0]?.id?.toString() || '');
        setActiveProjectOb(obs[0]);
        setCookie('panelmetrics_active_project_id', obs[0]?.id?.toString() || '');
      }
    }, (z) => {
      // setServerErrorSnackOpen(true);
    }, (!!login?.auth_token ? {auth_token: login?.auth_token, email: login?.email} : undefined));
  };

  useEffect(() => {
    if (!!login) fetchMyCompanies();
    if (!!login) fetchMyProjects();
  }, [login]);

  return (
    <Box sx={{
      display: "flex",
      flexDirection: "column",
      width: "100%",
      height:'100%',
      backgroundColor: theme.palette.primary?.main,
      margin: -1,
      padding: 1,
      paddingBottom: 2
    }}>
      {!!activeCompanyOb && !!activeCompanyOb.get_item && <Box sx={{
        marginLeft: -1,
        marginRight: -0.80,
        marginTop: -1
      }}>
        <img src={activeCompanyOb.get_item} style={{
          maxWidth: '100%'
        }} />
      </Box>}
      <Profile />
      {!!is_admin && <Box sx={{
        marginTop: 2,
        borderRadius: 1,
        border: '1px solid white',
        textAlign: 'center',
        padding: 0.5
      }} onClick={() => {
        if (!adminAsOrganiser && !activeCompany) {
          setActiveCompany(myCompanies[0]?.id?.toString());
          setActiveCompanyOb(myCompanies[0]);
          setCookie('panelmetrics_active_company_id', myCompanies[0]?.id?.toString());
        }
        setAdminAsOrganiser(!adminAsOrganiser);
        navigate("/");
      }}>
        {!!adminAsOrganiser && <Typography sx={{
          cursor: 'pointer',
          fontSize: '0.8rem',
          color: 'white'
        }}><SwitchAccountIcon sx={{marginRight: 1, height: '12px', width: '12px'}} />GoTo Admin-view</Typography>}
        {!adminAsOrganiser && <Typography sx={{
          cursor: 'pointer',
          fontSize: '0.8rem',
          color: 'white'
        }}><SwitchAccountIcon sx={{marginRight: 1, height: '12px', width: '12px'}} />GoTo Organiser-view</Typography>}
      </Box>}
      {!!login && (!!adminAsOrganiser || is_organiser) && <Box sx={{height: 50, paddingTop: 2, marginTop: 1}}>
        <Typography sx={{
          color: 'white',
          fontSize: '8px',
          marginTop: -1
        }}>Company picker</Typography>
        <StyledSelect
          label=""
          value={activeCompany}
          id='c_id'
          onChange={(v) => {
            setCookie('panelmetrics_active_company_id', v);
            setActiveCompany(v);
            setActiveCompanyOb(myCompanies.filter(p => p.id?.toString() === v)[0]);
            setCookie('panelmetrics_active_project_id', '');
            setActiveProject('');
            setActiveProjectOb(undefined);
            window.location.reload();
          }}
          list={[
            {id: '', name: 'None selected'},
            ...myCompanies.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => {return {id: (c.id || '').toString(), name: c.name};})
          ]}
          sx={{
            maxWidth: '185px',
            "& svg": {
              color: 'white'
            },
            "& div": {
              color: 'white',
              fontSize: '0.7rem'
            },
            "& fieldset": {
              borderColor: 'white !important'
            }
          }}
        />
      </Box>}

      <Box sx={{height: 40, paddingTop: 2, textAlign: 'right'}}>
        <SidebarQueues />
      </Box>

      <MenuItem path="/dashboard" label="Dashboard" icon={<SpeedIcon />} />
      
      {login?.is_special_permissions && !adminAsOrganiser && <MenuItem hidden={!is_admin} path="/companies" label="Companies" icon={<BusinessIcon />} />}
      {checkAllowed(login, null, null, "Admins", 'r', true, true) && !adminAsOrganiser && <MenuItem hidden={!is_admin} path="/organisers" label="Organisers" icon={<PeopleOutlineIcon />} />}

      {!!login && <Box sx={{marginTop: 2, backgroundColor: 'white', width: '100%', maxWidth: '180px', height: '1px'}} />}
      {!!login && (!adminAsOrganiser && !is_organiser) && <Box sx={{height: 50, paddingTop: 2}}>
        <Typography sx={{
          color: 'white',
          fontSize: '8px',
          marginTop: -1
        }}>Company modules</Typography>
        <StyledSelect
          label=""
          value={activeCompany}
          id='c_id'
          onChange={(v) => {
            setCookie('panelmetrics_active_company_id', v);
            setActiveCompany(v);
            setActiveCompanyOb(myCompanies.filter(p => p.id?.toString() === v)[0]);
            setCookie('panelmetrics_active_project_id', '');
            setActiveProject('');
            setActiveProjectOb(undefined);
            window.location.reload();
          }}
          list={[
            {id: '', name: 'None selected'},
            ...myCompanies.sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(c => {return {id: (c.id || '').toString(), name: c.name};})
          ]}
          sx={{
            maxWidth: '185px',
            "& svg": {
              color: 'white'
            },
            "& div": {
              color: 'white',
              fontSize: '0.7rem'
            },
            "& fieldset": {
              borderColor: 'white !important'
            }
          }}
        />
      </Box>}
      {!!activeCompany && (login?.is_organiser || (login?.is_admin && !!adminAsOrganiser)) && <MenuItem hidden={!is_admin && !is_organiser} path={`/userinvites`} label="Evolutie reacties" icon={<GroupAddIcon />} />}
      {/* {!!activeCompany && (login?.is_organiser || (login?.is_admin && !!adminAsOrganiser)) && <MenuItem hidden={!is_admin && !is_organiser} path="/mailer" label="Mailer" icon={<EmailIcon />} />} */}
      {!!activeCompany && !adminAsOrganiser && <MenuItem hidden={!is_admin} path="/mailer" label="Mailer" icon={<EmailIcon />} />}
      {!!activeCompany && !adminAsOrganiser && <MenuItem hidden={!is_admin} path="/organiser_logs" label="Logs" icon={<ListAltIcon />} />}
      {!!activeCompany && !adminAsOrganiser && <MenuItem hidden={!is_admin} path="/projects" label="Projects" icon={<WorkspacesIcon />} />}
      {!!activeCompany && <MenuItem hidden={!is_admin && !is_organiser} path={`/reporting`} label="Reports" icon={<StackedLineChartIcon />} />}
      {!!activeCompany && !adminAsOrganiser && <MenuItem hidden={!is_admin} path="/segments" label="Segments" icon={<ExtensionIcon />} />}

      {/* {(login?.is_special_permissions || (!!login && checkAllowed(login, null, null, "Projects", 'r'))) && <MenuItem hidden={!is_admin} path="/projects" label="Projects" icon={<WorkspacesIcon />} />}
      {(login?.is_special_permissions || (!!login && checkAllowed(login, null, null, "Users", 'r'))) && <MenuItem hidden={!is_admin} path="/users" label="Users" icon={<PeopleAltIcon />} />} */}
      
      {/* <MenuItem hidden={!is_admin} path="/exports" label="Exports" icon={<BarChartIcon />} /> */}
      
      {/* <MenuItem hidden={!is_admin} label="Platform" icon={<EngineeringIcon />}>
        <MenuItem hidden={!is_admin} path="/system_settings" label="System Settings" child />
      </MenuItem> */}

      {!!login && !!activeCompany && login?.is_admin && !login.is_memoq && !adminAsOrganiser && <Box sx={{marginTop: 2, backgroundColor: 'white', width: '100%', maxWidth: '180px', height: '1px'}} />}
      {!!login && !!activeCompany && login?.is_admin && !login.is_memoq && !adminAsOrganiser && <Box sx={{height: 50, paddingTop: 2}}>
        <Typography sx={{
          color: 'white',
          fontSize: '8px',
          marginTop: -1
        }}>Project modules</Typography>
        <StyledSelect
          label=""
          value={activeProject}
          id='p_id'
          onChange={(v) => {
            setCookie('panelmetrics_active_project_id', v);
            setActiveProject(v);
            setActiveProjectOb(myProjects.filter(p => p.id?.toString() === v)[0]);
          }}
          list={[
            {id: '', name: 'None selected'},
            ...myProjects.sort((a, b) => (a.name || '').localeCompare(b.name || '')).filter(fff => fff.company_id?.toString() === activeCompany).map(c => {return {id: (c.id || '').toString(), name: (c.name || '')};})
          ]}
          sx={{
            maxWidth: '185px',
            "& svg": {
              color: 'white'
            },
            "& div": {
              color: 'white',
              fontSize: '0.7rem'
            },
            "& fieldset": {
              borderColor: 'white !important'
            }
          }}
        />
      </Box>}

      {!!activeProject && myProjects.map(p => p.id?.toString()).indexOf(activeProject) > -1 && !login?.is_memoq && !adminAsOrganiser && <>
        {(checkAllowed(login, activeProjectOb?.company_id?.toString() || '0', activeProject, "ProjectDomains", 'r')) && <MenuItem hidden={!is_admin} path={`/domains/${activeProject}`} label="Domains" icon={<LinkIcon />} />}
        {(checkAllowed(login, activeProjectOb?.company_id?.toString() || '0', activeProject, "ProjectEmbeddables", 'r')) && <MenuItem hidden={!is_admin} path={`/embeddables/${activeProject}`} label="Embeddables" icon={<CodeIcon />} />}
        {(checkAllowed(login, activeProjectOb?.company_id?.toString() || '0', activeProject, "ProjectPages", 'r')) && <MenuItem hidden={!is_admin} path={`/pages/${activeProject}`} label="Pages" icon={<ArticleIcon />} />}
        {(checkAllowed(login, activeProjectOb?.company_id?.toString() || '0', activeProject, "ProjectQuestions", 'r')) && <MenuItem hidden={!is_admin} path={`/questions/${activeProject}`} label="Questions" icon={<QuestionMarkIcon />} />}
        {/* {<MenuItem hidden={!is_admin} path={`/reports/${activeProject}`} label="Reports" icon={<StackedLineChartIcon />} />} */}
        {(checkAllowed(login, activeProjectOb?.company_id?.toString() || '0', activeProject, "ProjectExport", 'r')) && <MenuItem hidden={!is_admin} path={`/stats/${activeProject}`} label="Stats & Export" icon={<PieChartIcon />} />}
        {<MenuItem hidden={!is_admin} path={`/user_invites/${activeProject}`} label="Evolutie reacties" icon={<GroupAddIcon />} />}
        {/* {!!activeCompany && <Box sx={{marginTop: 2, backgroundColor: 'white', width: '100%', maxWidth: '180px', height: '1px'}} />} */}
        {!!activeProjectOb && activeProjectOb.custom_key === '2024_taalklacht_dashboard' && <>
          <Box sx={{marginTop: 2, backgroundColor: 'white', width: '100%', maxWidth: '180px', height: '1px'}} />
          {<MenuItem hidden={!is_admin && !is_organiser} path={`/cust/taalklachten/dashboard`} label="TK Dashboard" icon={<BarChartIcon />} />}
          {<MenuItem hidden={!is_admin && !is_organiser} path={`/cust/taalklachten/list`} label="TK List" icon={<ChecklistIcon />} />}
        </>}
      </>}

      {login?.is_special_permissions && !login?.is_memoq && !adminAsOrganiser && <Box sx={{marginTop: 20, backgroundColor: 'white', width: '100%', maxWidth: '180px', height: '1px'}} />}
      
      {checkAllowed(login, null, null, "Admins", 'r', true, true) && !login?.is_memoq && !adminAsOrganiser && <MenuItem hidden={!is_admin} path="/users" label="Admins" icon={<PeopleAltIcon />} />}
      {login?.is_special_permissions && !login?.is_memoq && !adminAsOrganiser && <MenuItem hidden={!is_admin} label="Content" icon={<FormatListBulletedIcon />}>
        <MenuItem hidden={!is_admin} path="/cities" label="Cities" child />
        <MenuItem hidden={!is_admin} path={`/faqs/${activeProject}`} label="Faqs" child />
        {/* <MenuItem hidden={!is_admin} path="/mailer" label="Mailer" child /> */}
        <MenuItem hidden={!is_admin} path="/statics" label="Statics" child />
      </MenuItem>}
      {login?.is_special_permissions && !login?.is_memoq && !adminAsOrganiser && <MenuItem hidden={!is_admin} path="/exports" label="Exports" icon={<GetAppIcon />} />}
      {login?.is_special_permissions && !login?.is_memoq && !adminAsOrganiser && <MenuItem hidden={!is_admin} label="External" icon={<ArrowOutwardIcon />}>
        <MenuItem hidden={!is_admin} external='https://account.postmarkapp.com/servers' label="Postmark" child />
      </MenuItem>}
      {login?.is_special_permissions && !login?.is_memoq && !adminAsOrganiser  && <MenuItem hidden={!is_admin} path="/system_mailer" label="Mailer" icon={<EmailIcon />} />}
      {login?.is_special_permissions && !login?.is_memoq && !adminAsOrganiser && <MenuItem hidden={!is_admin} label="Special" icon={<GradeIcon />}>
        <MenuItem hidden={!is_admin} path="/special/memoq" label="MEMOQ" child />
      </MenuItem>}

      <Box sx={{
        marginTop: 2,
        textAlign: 'center'
      }}>
        <Avatar variant="square" src={PmTrans} sx={{
          width: '100%',
          height: '80px',
          "& img": {
            objectFit: 'contain'
          }
        }} />
      </Box>
    </Box>
  );
}